import React, { useCallback, useState, useRef, useEffect } from 'react'
import styled from 'styled-components/macro'
import { useNavigate } from 'react-router-dom'
import { PiCaretDown, PiCheck } from 'react-icons/pi'
import { getDropdownZIndex } from 'styles/z-index'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import type { BreadcrumbItem, BreadcrumbDropdownItem } from './types'

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  className?: string
}

const BreadcrumbsWrapper = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--nav-text-primary);
`

const BreadcrumbItemWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const BreadcrumbButton = styled.button<{
  $isClickable: boolean
  $isActive: boolean
}>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: none;
  background: ${({ $isActive }) =>
    $isActive ? 'var(--nav-bg-hover)' : 'transparent'};
  color: var(--nav-text-primary);
  font-size: 14px;
  font-weight: 400;
  border-radius: 6px;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  transition: background 0.15s ease-in-out;

  &:hover {
    background: ${({ $isClickable }) =>
      $isClickable ? 'var(--nav-bg-hover)' : 'transparent'};
  }

  svg {
    transition: transform 0.15s ease-in-out;
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }
`

const BreadcrumbLink = styled.a`
  padding: 6px 10px;
  color: var(--nav-text-primary);
  font-size: 14px;
  font-weight: 400;
  text-decoration: underline;
  border-radius: 6px;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: var(--nav-bg-hover);
    color: var(--nav-text-primary);
    text-decoration: underline;
  }
`

const BreadcrumbText = styled.span`
  padding: 6px 10px;
  color: var(--nav-text-primary);
  font-size: 14px;
  font-weight: 400;
`

const Separator = styled.span`
  color: var(--nav-text-muted);
  font-size: 14px;
  margin: 0 2px;
`

const Dropdown = styled.div<{ $zIndex: number }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: ${({ $zIndex }) => $zIndex};
`

const DropdownItem = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: ${({ $isActive }) =>
    $isActive ? 'rgba(165, 190, 175, 0.4)' : 'transparent'};
  color: #191d23;
  font-size: 14px;
  font-weight: ${({ $isActive }) => ($isActive ? '500' : '400')};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: var(--slide-out-hover);
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:only-child {
    border-radius: 8px;
  }
`

const DropdownItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const DropdownItemDescription = styled.span`
  font-size: 12px;
  color: #6e6e6e;
  font-weight: 300;
`

const CheckIcon = styled(PiCheck)`
  color: var(--nav-bg-primary);
  flex-shrink: 0;
`

interface BreadcrumbDropdownProps {
  item: BreadcrumbItem
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
}

const BreadcrumbDropdown: React.FC<BreadcrumbDropdownProps> = ({
  item,
  isOpen,
  onToggle,
  onClose,
}) => {
  const navigate = useNavigate()
  const modalDepth = useModalDepth()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  const handleItemClick = useCallback(
    (dropdownItem: BreadcrumbDropdownItem) => {
      navigate(dropdownItem.path)
      onClose()
    },
    [navigate, onClose]
  )

  if (!item.hasDropdown || !item.dropdownItems?.length) {
    return <BreadcrumbText>{item.label}</BreadcrumbText>
  }

  return (
    <BreadcrumbItemWrapper>
      <BreadcrumbButton
        ref={buttonRef}
        $isClickable={true}
        $isActive={isOpen}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {item.label}
        <PiCaretDown size={14} />
      </BreadcrumbButton>

      {isOpen && (
        <Dropdown ref={dropdownRef} $zIndex={zIndex}>
          {item.dropdownItems.map((dropdownItem, index) => (
            <DropdownItem
              key={index}
              $isActive={dropdownItem.isActive || false}
              onClick={() => handleItemClick(dropdownItem)}
            >
              <DropdownItemContent>
                <span>{dropdownItem.label}</span>
                {dropdownItem.description && (
                  <DropdownItemDescription>
                    {dropdownItem.description}
                  </DropdownItemDescription>
                )}
              </DropdownItemContent>
              {dropdownItem.isActive && <CheckIcon size={16} />}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </BreadcrumbItemWrapper>
  )
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  className,
}) => {
  const navigate = useNavigate()
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  )

  const handleToggle = useCallback((index: number) => {
    setOpenDropdownIndex((prev) => (prev === index ? null : index))
  }, [])

  const handleClose = useCallback(() => {
    setOpenDropdownIndex(null)
  }, [])

  if (!items.length) return null

  return (
    <BreadcrumbsWrapper
      className={className}
      aria-label="Breadcrumb navigation"
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Separator>/</Separator>}

          {item.hasDropdown ? (
            <BreadcrumbDropdown
              item={item}
              isOpen={openDropdownIndex === index}
              onToggle={() => handleToggle(index)}
              onClose={handleClose}
            />
          ) : item.path ? (
            <BreadcrumbLink
              href={item.path}
              onClick={(e) => {
                e.preventDefault()
                navigate(item.path!)
              }}
            >
              {item.label}
            </BreadcrumbLink>
          ) : (
            <BreadcrumbText>{item.label}</BreadcrumbText>
          )}
        </React.Fragment>
      ))}
    </BreadcrumbsWrapper>
  )
}
